"use client";

import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";

export function SidebarCompactOverlayPortal({
  overlay,
  onOverlayEnter,
  onOverlayLeave,
  isChildActive,
  onChildNavigate,
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !overlay) return null;

  if (overlay.type === "tip") {
    return createPortal(
      <div
        className="fixed z-[120] hidden -translate-y-1/2 items-center lg:flex"
        style={{ top: overlay.top, left: overlay.anchorRight - 6 }}
        onMouseEnter={onOverlayEnter}
        onMouseLeave={onOverlayLeave}
      >
        <div className="h-10 w-3 shrink-0" aria-hidden />
        <div
          role="tooltip"
          className="relative whitespace-nowrap rounded-lg bg-gray-800 px-3 py-1.5 text-sm font-medium tracking-wide text-white shadow-lg ring-1 ring-gray-700/60"
        >
          {overlay.label}
          <span
            className="absolute top-1/2 right-full -translate-y-1/2 border-y-[6px] border-r-[6px] border-y-transparent border-r-gray-800"
            aria-hidden
          />
        </div>
      </div>,
      document.body,
    );
  }

  if (overlay.type === "flyout") {
    return createPortal(
      <div
        className="fixed z-[120] hidden min-w-[210px] max-w-[260px] rounded-lg bg-gray-800 py-2 shadow-xl ring-1 ring-gray-700/60 lg:block"
        style={{ top: overlay.top, left: overlay.left }}
        onMouseEnter={onOverlayEnter}
        onMouseLeave={onOverlayLeave}
      >
        <div className="absolute top-0 right-full h-full w-4" aria-hidden />
        <div className="mb-1 border-b border-gray-700/50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
          {overlay.label}
        </div>
        <ul className="max-h-[min(70vh,22rem)] overflow-y-auto py-1">
          {overlay.children.map((child) =>
            child.href ? (
              <li key={child.id}>
                <Link
                  href={child.href}
                  prefetch={false}
                  onClick={onChildNavigate}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                    isChildActive?.(child)
                      ? "bg-gray-700/80 text-white"
                      : "text-gray-300 hover:bg-gray-700/50 hover:text-white"
                  }`}
                  aria-current={isChildActive?.(child) ? "page" : undefined}
                  rel="noopener noreferrer"
                >
                  {child.iconSrc ? (
                    <Image
                      src={child.iconSrc}
                      alt={child.iconAlt ?? ""}
                      width={16}
                      height={16}
                      className="h-4 w-4 flex-shrink-0 rounded-sm object-contain"
                    />
                  ) : null}
                  <span className="truncate font-medium tracking-wide">
                    {child.name}
                  </span>
                </Link>
              </li>
            ) : (
              <li
                key={child.id}
                className="px-3 py-2 text-sm font-medium tracking-wide text-gray-500"
              >
                {child.name}
              </li>
            ),
          )}
        </ul>
      </div>,
      document.body,
    );
  }

  return null;
}

export function createCompactTipHandlers(
  setOverlay,
  enabled,
  label,
  { scheduleHide, clearHide, setHoverAnchor },
) {
  if (!enabled || !label) return {};

  const show = (target) => {
    clearHide();
    setHoverAnchor?.(target);
    const rect = target.getBoundingClientRect();
    setOverlay({
      type: "tip",
      label,
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
      anchorRight: rect.right,
    });
  };

  return {
    onMouseEnter: (e) => show(e.currentTarget),
    onMouseLeave: scheduleHide,
    onFocus: (e) => show(e.currentTarget),
    onBlur: scheduleHide,
  };
}

export function createCompactFlyoutHandlers(
  setOverlay,
  enabled,
  item,
  { scheduleHide, clearHide, setHoverAnchor },
) {
  if (!enabled || !item?.children?.length) return {};

  const show = (target) => {
    clearHide();
    setHoverAnchor?.(target);
    const rect = target.getBoundingClientRect();
    const maxHeight = Math.min(window.innerHeight * 0.7, 352);
    const top = Math.max(8, Math.min(rect.top, window.innerHeight - maxHeight - 8));

    setOverlay({
      type: "flyout",
      id: item.id,
      label: item.name,
      children: item.children,
      top,
      left: rect.right + 10,
      anchorRight: rect.right,
    });
  };

  return {
    onMouseEnter: (e) => show(e.currentTarget),
    onMouseLeave: scheduleHide,
    onFocus: (e) => show(e.currentTarget),
    onBlur: scheduleHide,
  };
}

/** @deprecated Use SidebarCompactOverlayPortal */
export function SidebarCompactTooltipPortal({ tip }) {
  return (
    <SidebarCompactOverlayPortal
      overlay={tip ? { type: "tip", ...tip } : null}
      onOverlayEnter={() => {}}
      onOverlayLeave={() => {}}
    />
  );
}
